/**
 * Please note! This file is a very thin client wrapper around the API.
 * It does not support yet Swagger / OpenAPI, so it is modified manually, but in future
 * it will be autogenerated automatically.
 * To ensure that transition is nice and simple, please do not add more responsibilities here.
 * Models here should only reflect the API models, there should not be any response processing
 * Or request validations
 */

import { AxiosPromise, AxiosRequestConfig } from 'axios';

import { axios } from './axios';
import {
  AuthCodeCheckResponse,
  AuthRefreshRequest,
  AuthResult,
  ChangeFavoriteRequest,
  ChannelResponse,
  CoreMenuConfig,
  DeviceInfoRequest,
  FavoritesResponse,
  FeedResponse,
  FieldsResponse,
  GiftProductsResponse,
  HistoryAllResponse,
  LiveNowResponse,
  LocalesResponse,
  LoginRequest,
  MediaType,
  MvpdAuthRequest,
  MvpdAuthResponse,
  MvpdResponse,
  OauthTokenResponse,
  OrderDetails,
  Preferences,
  PreferencesResponse,
  ProductsResponse,
  ProfileResponse,
  RegisterRequest,
  ResourceType,
  ScheduleResponse,
  SensitiveAction,
  SubmitResponse,
  TFormData,
  TokenResponse,
  TransactionOfferDetails,
  TranslationResponse,
  WatchHistoryItemResponse,
} from './models';

export const userService = {
  login(
    body: LoginRequest,
    $config?: AxiosRequestConfig
  ): AxiosPromise<AuthResult> {
    const url = '/api/core/auth/login';

    return axios.request<AuthResult>({
      url: url,
      method: 'POST',
      data: body,
      ...$config,
    });
  },

  /** Retrieves information about the authentication code used for Second Screen Auth */
  getAuthCodeInfo(
    code: string,
    $config?: AxiosRequestConfig
  ): AxiosPromise<AuthCodeCheckResponse> {
    const url = `/api/core/auth/code/${code}`;

    return axios.request<AuthCodeCheckResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  refresh(
    request?: AuthRefreshRequest,
    $config?: AxiosRequestConfig
  ): AxiosPromise<AuthResult> {
    const url = '/api/auth/refresh';

    return axios.request<AuthResult>({
      url: url,
      method: 'POST',
      data: request,
      ...$config,
    });
  },

  getProfile($config?: AxiosRequestConfig): AxiosPromise<ProfileResponse> {
    const url = '/api/core/user/profile';

    return axios.request<ProfileResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  getPreferences($config?: AxiosRequestConfig) {
    const url = '/api/core/user/profile/preferences';

    return axios.request<PreferencesResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  updatePreferences(preferences: Preferences, $config?: AxiosRequestConfig) {
    const url = '/api/core/user/profile/preferences';

    return axios.request({
      url: url,
      method: 'PATCH',
      data: preferences,
      ...$config,
    });
  },

  logout($config?: AxiosRequestConfig): AxiosPromise<void> {
    const url = '/api/logout';

    return axios.request<void>({
      url: url,
      method: 'POST',
      data: null,
      ...$config,
    });
  },

  getAnonymousToken(
    body: DeviceInfoRequest,
    $config?: AxiosRequestConfig
  ): AxiosPromise<TokenResponse> {
    const url = '/api/core/hello';

    return axios.request<TokenResponse>({
      url: url,
      method: 'POST',
      data: body,
      ...$config,
    });
  },

  getOauthToken(
    $config?: AxiosRequestConfig
  ): AxiosPromise<OauthTokenResponse> {
    const url = '/api/core/auth/token';

    return axios.request<OauthTokenResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  getLoginInputs($config?: AxiosRequestConfig): AxiosPromise<FieldsResponse> {
    const url = '/api/core/auth/login';

    return axios.request<FieldsResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  getRegisterInputs(
    $config?: AxiosRequestConfig
  ): AxiosPromise<FieldsResponse> {
    const url = '/api/core/auth/register';

    return axios.request<FieldsResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  submitRegistration(
    body: RegisterRequest,
    $config?: AxiosRequestConfig
  ): AxiosPromise<AuthResult> {
    const url = '/api/core/auth/register';

    return axios.request<AuthResult>({
      url: url,
      method: 'POST',
      data: body,
      ...$config,
    });
  },

  getUpdateUserInputs(
    $config?: AxiosRequestConfig
  ): AxiosPromise<FieldsResponse> {
    const url = '/api/core/user/profile/update';

    return axios.request<FieldsResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  /**
   * Updates the whole user profile. Please send full profile information.
   * Partial updates may not work as expected
   */
  submitUpdateUser(body: TFormData, $config?: AxiosRequestConfig) {
    const url = '/api/core/user/profile/update';

    return axios.request<SubmitResponse>({
      url: url,
      method: 'PUT',
      data: body,
      ...$config,
    });
  },

  getForgotPasswordInputs(
    $config?: AxiosRequestConfig
  ): AxiosPromise<FieldsResponse> {
    const url = '/api/core/auth/forgot-password';

    return axios.request<FieldsResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  submitForgotPassword(body: TFormData, $config?: AxiosRequestConfig) {
    const url = '/api/core/auth/forgot-password';

    return axios.request<SubmitResponse>({
      url: url,
      method: 'POST',
      data: body,
      ...$config,
    });
  },

  getUpdatePasswordInputs(
    $config?: AxiosRequestConfig
  ): AxiosPromise<FieldsResponse> {
    const url = '/api/core/auth/update-password';

    return axios.request<FieldsResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  submitUpdatePassword(body: TFormData, $config?: AxiosRequestConfig) {
    const url = '/api/core/auth/update-password';

    return axios.request<SubmitResponse>({
      url: url,
      method: 'POST',
      data: body,
      ...$config,
    });
  },

  getChangePasswordInputs(
    $config?: AxiosRequestConfig
  ): AxiosPromise<FieldsResponse> {
    const url = '/api/core/user/profile/change-password';

    return axios.request<FieldsResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  submitChangePassword(
    // the contents of the form depends on the GET profile/change-password response.
    body: TFormData,
    $config?: AxiosRequestConfig
  ) {
    const url = '/api/core/user/profile/change-password';

    return axios.request<SubmitResponse>({
      url,
      method: 'POST',
      data: body,
      ...$config,
    });
  },

  getSensitiveConfirmInputs(
    action: SensitiveAction,
    $config?: AxiosRequestConfig
  ): AxiosPromise<FieldsResponse> {
    const url = `/api/core/user/sensitive/confirm-action?action=${action}`;

    return axios.request<FieldsResponse>({
      url,
      method: 'GET',
      ...$config,
    });
  },

  submitSensitiveConfirm(
    body: TFormData,
    action: SensitiveAction,
    $config?: AxiosRequestConfig
  ) {
    const url = `/api/core/user/sensitive/confirm-action?action=${action}`;

    return axios.request<SubmitResponse>({
      url: url,
      method: 'POST',
      data: body,
      ...$config,
    });
  },
};

export const favoritesService = {
  getAll($config?: AxiosRequestConfig): AxiosPromise<FavoritesResponse> {
    const url = '/api/core/user/favorites/all';

    return axios.request<FavoritesResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  add(body: ChangeFavoriteRequest, $config?: AxiosRequestConfig): AxiosPromise {
    const url = '/api/core/user/favorites';

    return axios.request({
      url: url,
      method: 'POST',
      data: body,
      ...$config,
    });
  },

  remove(
    body: ChangeFavoriteRequest,
    $config?: AxiosRequestConfig
  ): AxiosPromise {
    const url = '/api/core/user/favorites';

    return axios.request({
      url: url,
      method: 'DELETE',
      data: body,
      ...$config,
    });
  },
};

export const historyService = {
  // do not use directly. use via historyActions to keep the cache updated.
  getAll($config?: AxiosRequestConfig): AxiosPromise<HistoryAllResponse> {
    const url = '/api/core/user/watch-history/all';

    return axios.request<HistoryAllResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  // do not use directly. use via historyActions to keep the cache updated.
  getPage(
    page: number,
    $config?: AxiosRequestConfig
  ): AxiosPromise<FeedResponse> {
    const url = '/api/core/user/watch-history';

    return axios.request<FeedResponse>({
      url: url,
      method: 'GET',
      params: {
        page,
      },
      ...$config,
    });
  },

  // do not use directly. use via historyActions to keep the cache updated.
  getOne(
    resourceType?: ResourceType,
    resourceId?: string,
    $config?: AxiosRequestConfig
  ): AxiosPromise<WatchHistoryItemResponse> {
    const url = `/api/core/user/watch-history/${resourceType}/${resourceId}`;

    return axios.request<WatchHistoryItemResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  // do not use directly. use via historyActions to keep the cache updated.
  remove(
    itemId?: string,
    mediaType?: MediaType,
    $config?: AxiosRequestConfig
  ): AxiosPromise {
    const url = `/api/core/user/watch-history/${mediaType}/${itemId}`;

    return axios.request({
      url: url,
      method: 'DELETE',
      ...$config,
    });
  },
};

export const localeService = {
  getLocales($config?: AxiosRequestConfig): AxiosPromise<LocalesResponse> {
    const url = '/api/core/i18n/locales';

    return axios.request({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  setLocale(locale: string, $config?: AxiosRequestConfig): AxiosPromise<void> {
    const url = `/api/locale/${locale}`;

    return axios.request({
      url: url,
      method: 'POST',
      ...$config,
    });
  },

  getTranslation(
    locale: string,
    $config?: AxiosRequestConfig
  ): AxiosPromise<TranslationResponse> {
    const url = `/api/core/i18n/translations/${locale}`;

    return axios.request({
      url: url,
      method: 'GET',
      ...$config,
    });
  },
};

export const configService = {
  getMenuConfig($config?: AxiosRequestConfig): AxiosPromise<CoreMenuConfig> {
    const url = '/api/core/menu';

    return axios.request({
      url: url,
      method: 'GET',
      ...$config,
    });
  },
};

export const epgService = {
  getChannels(
    page: number = 1,
    $config?: AxiosRequestConfig
  ): AxiosPromise<ChannelResponse> {
    const url = '/api/core/catalog/channels';

    return axios.request<ChannelResponse>({
      url: url,
      method: 'GET',
      params: {
        page,
        pageSize: 50,
      },
      ...$config,
    });
  },

  /**
   * Lists the live channels. If a channel has live program broadcasting now,
   * then return the live program instead of the channel
   * @see [Docs](https://docs.one.accedo.io/one-core/delivery-api/video-catalog/live-now/)
   */
  getLiveNow(
    page: number = 1,
    pageSize: number = 20,
    $config?: AxiosRequestConfig
  ): AxiosPromise<LiveNowResponse> {
    const url = '/api/core/catalog/live-now';

    return axios.request<LiveNowResponse>({
      url: url,
      method: 'GET',
      params: {
        page,
        pageSize,
      },
      ...$config,
    });
  },

  getSchedule(
    channelId: string,
    from: string,
    until: string,
    pageNumber?: number,
    refsSchedule?: string,
    $config?: AxiosRequestConfig
  ): AxiosPromise<ScheduleResponse> {
    const url =
      refsSchedule ?? `/api/core/catalog/channels/${channelId}/schedule`;
    const params: AxiosRequestConfig['params'] = {
      from,
      until,
    };

    if (pageNumber) {
      params.page = pageNumber;
    }

    return axios.request<ScheduleResponse>({
      url: url,
      method: 'GET',
      params,
      ...$config,
    });
  },
};

export const tveService = {
  /**
   * Lists available login methods for the TVE
   * @see https://docs.one.accedo.io/one-core/delivery-api/identity/tve/#get-mvpds
   */
  getMvpds($config?: AxiosRequestConfig): AxiosPromise<MvpdResponse> {
    const url = '/api/core/auth/tve/mvpds';

    return axios.request<MvpdResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  /**
   * Checks authentication of user or authenticates user using the selected login method
   * @see https://docs.one.accedo.io/one-core/delivery-api/identity/tve/#authenticate-via-tve
   * @param request (optional) MVPD request body. See model for more details.
   */
  authenticate(
    request: MvpdAuthRequest,
    $config?: AxiosRequestConfig
  ): AxiosPromise<MvpdAuthResponse> {
    const url = '/api/core/auth/tve/authenticate';

    return axios.request<MvpdAuthResponse>({
      url: url,
      method: 'POST',
      data: request,
      ...$config,
    });
  },

  /**
   * @see https://docs.one.accedo.io/one-core/delivery-api/identity/tve/#logout
   */
  logout($config?: AxiosRequestConfig): AxiosPromise<void> {
    const url = '/api/core/auth/logout';

    return axios.request<void>({
      url: url,
      method: 'POST',
      ...$config,
    });
  },
};

export const purchaseService = {
  getProducts(
    entitlements?: string[],
    $config?: AxiosRequestConfig
  ): AxiosPromise<ProductsResponse> {
    const url = '/api/core/purchase/in-app/web/products';

    const params: AxiosRequestConfig['params'] = {};

    if (entitlements?.length) {
      params.granting = entitlements.join(',');
    }

    return axios.request<ProductsResponse>({
      url: url,
      method: 'GET',
      params,
      ...$config,
    });
  },

  trackPurchase(
    {
      status,
      message,
      orderDetails,
      transactionalOffer,
      attributionTag,
      timestamp = new Date().toISOString(),
    }: {
      status: 'SUCCESS' | 'FAILURE' | 'PENDING';
      message: string;
      orderDetails: OrderDetails;
      transactionalOffer?: TransactionOfferDetails;
      attributionTag?: string;
      timestamp?: string;
    },
    $config?: AxiosRequestConfig
  ) {
    const url = '/api/core/purchase/in-app/web/validate';

    axios
      .request({
        url: url,
        method: 'POST',
        data: {
          status: {
            status: status,
            message: message,
          },
          orderDetails: {
            ...orderDetails,
            timestamp,
          },
          transactionalOffer,
          source: {
            attributionTag: attributionTag,
          },
        },
        ...$config,
      })
      .catch((e) => {
        //Intentionally swallowing error as this is a fire-and-forget method
        console.error('Failed to track purchase', e);
      });
  },

  alternativePurchase(
    transactionToken?: string,
    bundleIdentifier?: string,
    $config?: AxiosRequestConfig
  ): AxiosPromise<void> {
    const url = '/api/core/purchase/alternative/validate';

    return axios.request<any>({
      url: url,
      method: 'POST',
      data: {
        transactionToken,
        bundleIdentifier,
      },
      ...$config,
    });
  },
};

/**
 * @see https://docs.one.accedo.io/one-core/delivery-api/purchases/iap-gift/
 */
export const giftService = {
  getGiftProducts(
    $config?: AxiosRequestConfig
  ): AxiosPromise<GiftProductsResponse> {
    const url = '/api/core/purchase/in-app/web/gift/products';

    return axios.request<GiftProductsResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  getGiftInputs($config?: AxiosRequestConfig): AxiosPromise<FieldsResponse> {
    const url = '/api/core/purchase/in-app/web/gift/form';

    return axios.request<FieldsResponse>({
      url: url,
      method: 'GET',
      ...$config,
    });
  },

  /**
   * API call for validating the gift form input values.
   * @returns 200 status code and an empty body - if the validation is successful.
   * @returns 400 status code and `SubmitResponse` response type otherwise
   */
  validateGiftInputs(
    body: TFormData,
    $config?: AxiosRequestConfig
  ): AxiosPromise<SubmitResponse> {
    const url = '/api/core/purchase/in-app/web/gift/form';

    return axios.request({
      url: url,
      method: 'POST',
      data: body,
      ...$config,
    });
  },
};
